import React, {FC, useContext} from 'react';
import {PhotoPayloadBloom} from 'services';
import {RiDownloadCloud2Line, RiErrorWarningLine, RiExternalLinkLine, RiRobotLine} from "react-icons/ri";
import {ModerationUser} from "interfaces";
import {PhotoView} from "react-photo-view";
import {usePhotoManagement} from "components/ModerationUserCard/ModerationPhotos/usePhotoManagement";
import {FaRegTrashAlt} from "react-icons/fa";
import {getPhotoTags, isNoFaceButtonSupported, UserReviewTags, UserStatuses} from "enums";
import {
    ModerationPhotoTags
} from "components/ModerationUserCard/ModerationPhotos/ModerationPhotoTags/ModerationPhotoTags";
import {MdFaceRetouchingOff} from "react-icons/md";
import {AuthContext} from "contexts";

interface Props {
    // Full user object - if provided, will be used to derive userId, reviewTags, duplicateFaces, duplicateHashes, hasNoFace
    user?: ModerationUser;

    // Individual props - only needed when user is not available
    userId?: string; // Required when user is not provided (e.g., BlackListSearchPage)
    featureId?: number;
    pic: PhotoPayloadBloom;
    height?: number;
    className?: string;
    onPhotoDeleted?: (key: string) => void;
    onSensitiveTagChanged?: (key: string, isSensitive: boolean) => void;
    onAITagChanged?: (key: string, isOn: boolean) => void;
    onNoFaceChanged?: (key: string, hasFace: boolean) => void;
    objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
    isInTable?: boolean;
    style?: React.CSSProperties
}

export const ModerationPic: FC<Props> = ({
                                             user,
                                             onAITagChanged,
                                             objectFit,
                                             pic: p,
                                             height,
                                             className,
                                             userId: userIdProp,
                                             featureId,
                                             onPhotoDeleted,
                                             onSensitiveTagChanged,
                                             onNoFaceChanged,
                                             isInTable = false,
                                             style
                                         }) => {
    const photoManager = usePhotoManagement()
    const {env} = useContext(AuthContext);

    // Derive values from user if available, otherwise use individual props
    const userId = user?.id ?? userIdProp;
    if (!userId) {
        console.warn('ModerationPic: userId is required either via user prop or userId prop');
    }

    // All these values come from user if available
    const photoTags = user ? getPhotoTags(user, p.key) : [];
    const duplicateFaces = user?.duplicateFaces ?? [];
    const hasNoFace = user ? !!photoTags.find(t => t.tag === UserReviewTags.NoFace) : false;

    // Determine if callbacks should be disabled based on user status
    const shouldDisableCallbacks = user?.status === UserStatuses.BlackList;
    const effectiveOnSensitiveTagChanged = shouldDisableCallbacks ? undefined : onSensitiveTagChanged;
    const effectiveOnPhotoDeleted = shouldDisableCallbacks ? undefined : onPhotoDeleted;
    const effectiveOnNoFaceChanged = shouldDisableCallbacks ? undefined : onNoFaceChanged;
    const duplicateUsers = duplicateFaces.find(f => f.photoKey === p.key)?.users.filter(u => u.user.key !== userId) ?? [];

    const img = encodeURIComponent(p.image[0].link);
    const searchLinks = [
        {label: 'Google', link: `https://www.google.com/searchbyimage?client=app&image_url=${img}`},
        {label: 'G. Image', link: `https://lens.google.com/uploadbyurl?url=${img}`},
        {label: 'Yandex', link: `https://yandex.com/images/search?rpt=imageview&url=${img}`}
    ]

    if (!p || !p.image || !p.image.length) {
        return null;
    }

    const toggleSensitive = async () => {
        const result = await photoManager.toggleSensitive(p, userId, {objectFit, withAlert: true});
        if (result === true || result === false) {
            effectiveOnSensitiveTagChanged && effectiveOnSensitiveTagChanged(p.key, !p.isSensitive);
        }
    };

    const toggleAI = async () => {
        const result = await photoManager.toggleAI(p, !p.isAiGenerated, userId, {objectFit, withAlert: true});
        if ((result === true || result === false) && onAITagChanged) {
            const newAIGenerated = !p.isAiGenerated;
            onAITagChanged(p.key, newAIGenerated)
        }
    };

    const toggleNoFace = async () => {
        const result = await photoManager.toggleNoFace(p, hasNoFace, userId, {objectFit, withAlert: true});
        if (result === true || result === false) {
            effectiveOnNoFaceChanged && effectiveOnNoFaceChanged(p.key, !hasNoFace);
        }
    };

    const searchImage = () => {
        searchLinks.forEach(({link}, index) => {
            const newWindow = window.open(link);
            window.focus();
            if (!newWindow) {
                alert('Некоторые ссылки не открылись. Проверьте pop-up blocker и попробуйте снова.');
            }
        });
        window.focus();
        return false
    }

    const downloadImage = async () => {
        const originalImageUrl = p.image[0].link?.split('?')[0];
        window.open(originalImageUrl, '_blank', 'noopener,noreferrer');
    }

    const photo = (
        <div className="d-flex justify-content-center">
            <div className="position-relative d-inline-block">
                <img
                    src={p.image[0].link}
                    alt={`Pic ${p.key}`}
                    style={{
                        objectFit: objectFit ?? 'contain',
                        height: 'auto',
                        maxHeight: height ?? 400,
                        ...style
                    }}
                    className={`img-fluid d-block rounded ${className}`}
                />
                <div
                    className="position-absolute d-flex flex-row flex-wrap"
                    style={{top: isInTable ? 4 : 12, right: isInTable ? 4 : 12, gap: 4}}
                >
                    {p.isPrivate && (
                        <span className="badge bg-secondary rounded-pill">Private</span>
                    )}
                    {p.isAiGenerated && (
                        <span className="badge bg-primary rounded-pill">{isInTable ? 'AI' : 'AI Генерация'}</span>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="m-1 rounded bg-secondary h-100 d-flex flex-column justify-content-between">
            {(photoTags.length > 0 || duplicateUsers.length > 0) &&
                <div
                    className="d-flex flex-column mx-2 pt-2 pb-2"
                    style={{top: 12, right: 12, gap: 2}}>
                    {photoTags.length > 0 &&
                        <ModerationPhotoTags reviewTags={photoTags}
                                             isSensitive={isInTable ? false : p.isSensitive}
                                             hasNoFace={hasNoFace ?? false}/>
                    }
                    {duplicateUsers.length > 0 &&
                        <span className={`badge text-wrap text-start text-black`}
                              style={{fontWeight: 400}}>{`${duplicateUsers.length} дубликат(а/ов)`}</span>
                    }
                </div>
            }

            {isInTable ? photo :
                <PhotoView src={p.image[0].link}>
                    {photo}
                </PhotoView>
            }
            {!isInTable &&
                <div className="d-flex flex-row flex-wrap align-items-start pt-1 pb-1 mx-1"
                     style={{bottom: 0, right: 0, gap: 4}}>
                    {effectiveOnNoFaceChanged && isNoFaceButtonSupported(env) &&
                        <button type="button"
                                className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center"
                                onClick={toggleNoFace}>
                            <MdFaceRetouchingOff size={14}
                                                 style={{marginRight: 4}}/>
                            {hasNoFace ? 'Удалить NoFace' : 'Добавить NoFace'}
                        </button>}
                    {effectiveOnSensitiveTagChanged &&
                        <button type="button"
                                className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center"
                                onClick={toggleSensitive}>
                            <RiErrorWarningLine size={14} style={{marginRight: 4}} color={p.isSensitive ? 'red' : ''}/>
                            {p.isSensitive ? "Удалить Sensitive" : "Добавить Sensitive"}
                        </button>}
                    {onAITagChanged &&
                        <button type="button"
                                className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center"
                                onClick={toggleAI}>
                            <RiRobotLine size={14} style={{marginRight: 4}}/>
                            {p.isAiGenerated ? 'Удалить AI тег' : 'Добавить AI тег'}
                        </button>}
                    <button type="button"
                            className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center"
                            onClick={searchImage}>
                        <RiExternalLinkLine size={14} style={{marginRight: 4}}/>Проверить
                    </button>
                    <button type="button"
                            className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center"
                            onClick={downloadImage}>
                        <RiDownloadCloud2Line size={14} style={{marginRight: 4}}/>Скачать
                    </button>
                    {effectiveOnPhotoDeleted &&
                        <button type="button"
                                className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center"
                                onClick={async () => {
                                    const result = await photoManager.onPicPreview(p, userId, featureId)
                                    result && effectiveOnPhotoDeleted && effectiveOnPhotoDeleted(result.toString())
                                }}>
                            <FaRegTrashAlt size={14} style={{marginRight: 4}}/>
                            Удалить
                        </button>
                    }
                </div>
            }
        </div>
    );
};
